import {
  html,
  attr,
  prop,
  Signal,
  When,
  on,
  Value,
  Fragment,
  ForEach,
} from '@tempots/dom'
import { Anchor } from '@tempots/ui'
import { Button, Icon, Modal } from '@tempots/beatui'
import { PageLayout } from '@/components/layout'
import { DataTable } from '@/components/data'
import { LoadingState } from '@/components/ui/loading-state'
import { ErrorState } from '@/components/ui/error-state'
import { StatusBadge, type BadgeVariant } from '@/components/ui/status-badge'
import { capitalize, formatCredits } from '@/utils/formatting'
import {
  organizations as orgsService,
  credits as creditsService,
} from '@/services/gateway'
import type { OrgCreditTransaction, CreditPack } from '@/services/types'

// ============================================================================
// Types
// ============================================================================

interface Organization {
  id: string
  name: string
  isPersonal: boolean
  creditBalance: bigint
}

interface Transaction {
  id: string
  type: 'purchase' | 'usage' | 'refund'
  description: string
  amount: number
  timestamp: string
}

// ============================================================================
// Organization Billing Page Component
// ============================================================================

export interface OrganizationBillingPageOptions {
  id: Value<string>
}

export function OrganizationBillingPage({
  id,
}: OrganizationBillingPageOptions) {
  const org = prop<Organization | null>(null)
  const transactions = prop<Transaction[]>([])
  const isLoading = prop(true)
  const error = prop<string | null>(null)

  // Purchase state
  const purchasing = prop(false)
  const purchaseError = prop<string | null>(null)
  const creditPacks = prop<CreditPack[]>([])
  const selectedPackId = prop<string | null>(null)
  const loadingPacks = prop(false)

  // Load data
  loadOrganization()

  async function loadOrganization() {
    isLoading.set(true)
    error.set(null)

    try {
      const orgId = typeof id === 'string' ? id : id.get()
      const orgData = await orgsService.getOrganization(orgId)

      // Get credit history
      const txnList: Transaction[] = []
      try {
        const creditsData = await orgsService.getCreditHistory(orgId)

        /* eslint-disable tempots/require-async-signal-disposal -- plain array, not signal */
        txnList.push(
          ...creditsData.transactions.map((t: OrgCreditTransaction) => ({
            id: t.id,
            type: t.type as 'purchase' | 'usage' | 'refund',
            description: t.description || t.type,
            amount: t.amount,
            timestamp: t.createdAt,
          }))
        )
        /* eslint-enable tempots/require-async-signal-disposal */
      } catch {
        // Ignore transaction fetch errors
      }

      org.set({
        id: orgData.id,
        name: orgData.name,
        isPersonal: orgData.isPersonal,
        creditBalance: orgData.creditBalance,
      })
      transactions.set(txnList)
    } catch (err) {
      error.set(
        err instanceof Error ? err.message : 'Failed to load organization'
      )
    } finally {
      isLoading.set(false)
    }
  }

  async function loadCreditPacks() {
    loadingPacks.set(true)
    try {
      const response = await creditsService.getPacks()
      creditPacks.set(response.packs)
    } catch {
      // Ignore credit pack fetch errors
    } finally {
      loadingPacks.set(false)
    }
  }

  const handlePurchaseCredits = async (packId: string, close: () => void) => {
    const orgData = org.get()
    if (!orgData || !packId) return

    purchasing.set(true)
    purchaseError.set(null)

    try {
      const returnUrl = window.location.href

      const result = await orgsService.purchaseCredits(
        orgData.id,
        packId,
        returnUrl
      )

      // In dev mode, the checkoutUrl will be returnUrl?success=true
      // In production, it will be a Stripe checkout URL
      if (result.checkoutUrl) {
        if (result.checkoutUrl.includes('success=true')) {
          // Dev mode - credits already added, just reload
          close()
          loadOrganization()
        } else {
          // Production - redirect to Stripe
          window.location.href = result.checkoutUrl
        }
      }
    } catch (err) {
      purchaseError.set(
        err instanceof Error ? err.message : 'Failed to purchase credits'
      )
    } finally {
      purchasing.set(false)
    }
  }

  const orgId = typeof id === 'string' ? id : id.get()

  return PageLayout({
    header: html.div(
      attr.class('flex items-center gap-3'),
      Anchor(
        { href: `/o/${orgId}` },
        attr.class('text-gray-400 hover:text-gray-600'),
        Icon({ icon: 'mdi:chevron-left' }, attr.class('w-5 h-5'))
      ),
      html.span(
        org.map((o) => o?.name || 'Organization'),
        html.span(attr.class('text-gray-400'), ' / Billing')
      )
    ),
    content: Fragment(
      // Loading state
      When(isLoading, () => LoadingState({ size: 'lg' })),
      // Error state
      When(
        error.map((e) => e !== null),
        () => ErrorState({ error })
      ),
      // Main content
      When(
        org.map((o) => o !== null),
        () =>
          html.div(
            attr.class('space-y-6'),
            // Error message
            When(
              purchaseError.map((e) => e !== null),
              () => ErrorState({ error: purchaseError })
            ),
            // Credit Balance Card
            html.div(
              attr.class('bg-white rounded-xl border border-gray-200 p-6'),
              html.div(
                attr.class('flex items-start justify-between mb-6'),
                html.div(
                  html.h2(
                    attr.class('text-lg font-semibold text-gray-900'),
                    'Credit Balance'
                  ),
                  html.p(
                    attr.class('text-3xl font-bold text-gray-900 mt-2'),
                    org.map((o) => formatCredits(o?.creditBalance ?? 0))
                  )
                ),
                Modal({ size: 'md', dismissable: true }, (open, close) =>
                  html.div(
                    Button(
                      {
                        onClick: () => {
                          selectedPackId.set(null)
                          purchaseError.set(null)
                          loadCreditPacks()
                          open({
                            header: 'Purchase Credits',
                            body: html.div(
                              attr.class('space-y-4'),
                              html.p(
                                attr.class('text-sm text-gray-600'),
                                'Select a credit pack to purchase.'
                              ),
                              // Error in modal
                              When(
                                purchaseError.map((e) => e !== null),
                                () => ErrorState({ error: purchaseError })
                              ),
                              // Loading packs
                              When(loadingPacks, () =>
                                LoadingState({ size: 'sm' })
                              ),
                              // Pack selection grid
                              html.div(
                                attr.class(
                                  'grid grid-cols-1 sm:grid-cols-2 gap-3'
                                ),
                                ForEach(creditPacks, (pack) =>
                                  html.button(
                                    on.click(() =>
                                      selectedPackId.set(pack.get().id)
                                    ),
                                    attr.class(
                                      selectedPackId.map((selected) =>
                                        selected === pack.get().id
                                          ? 'p-4 rounded-lg border-2 border-brand-600 bg-brand-50 text-left cursor-pointer transition-all'
                                          : 'p-4 rounded-lg border-2 border-gray-200 hover:border-gray-300 text-left cursor-pointer transition-all'
                                      )
                                    ),
                                    html.div(
                                      attr.class(
                                        'flex items-center justify-between mb-2'
                                      ),
                                      html.span(
                                        attr.class(
                                          'font-semibold text-gray-900'
                                        ),
                                        pack.map((p) => p.name)
                                      ),
                                      html.span(
                                        attr.class(
                                          'text-brand-600 font-medium'
                                        ),
                                        pack.map(
                                          (p) =>
                                            `$${p.priceUsd.toLocaleString()}`
                                        )
                                      )
                                    ),
                                    html.p(
                                      attr.class(
                                        'text-2xl font-bold text-gray-900'
                                      ),
                                      pack.map((p) =>
                                        formatCredits(p.credits, {
                                          includeDecimals: false,
                                        })
                                      )
                                    ),
                                    html.p(
                                      attr.class('text-xs text-gray-500 mt-1'),
                                      pack.map(
                                        (p) =>
                                          `$${((p.priceUsd / p.credits) * 100).toFixed(2)} per 100 credits`
                                      )
                                    )
                                  )
                                )
                              )
                            ),
                            footer: html.div(
                              attr.class('flex justify-between gap-3 w-full'),
                              Button(
                                { variant: 'light', onClick: close },
                                'Cancel'
                              ),
                              Button(
                                {
                                  disabled: selectedPackId.flatMap((packId) =>
                                    purchasing.map((p) => p || packId === null)
                                  ),
                                  onClick: () => {
                                    const packId = selectedPackId.get()
                                    if (packId) {
                                      handlePurchaseCredits(packId, close)
                                    }
                                  },
                                },
                                purchasing.map((p) =>
                                  p ? 'Processing...' : 'Continue to Checkout'
                                )
                              )
                            ),
                          })
                        },
                      },
                      Icon({ icon: 'mdi:plus' }, attr.class('w-4 h-4 mr-2')),
                      'Purchase Credits'
                    )
                  )
                )
              )
            ),
            // Transaction History
            html.div(
              attr.class(
                'bg-white rounded-xl border border-gray-200 overflow-hidden'
              ),
              html.div(
                attr.class('px-6 py-4 border-b border-gray-200'),
                html.h2(
                  attr.class('text-lg font-semibold text-gray-900'),
                  'Transaction History'
                )
              ),
              DataTable<Transaction>({
                columns: [
                  {
                    key: 'description',
                    header: 'Description',
                    render: (item) =>
                      html.div(
                        html.p(
                          attr.class('font-medium text-gray-900'),
                          item.map((t) => t.description)
                        ),
                        html.p(
                          attr.class('text-sm text-gray-500'),
                          item.map((t) =>
                            new Date(t.timestamp).toLocaleString()
                          )
                        )
                      ),
                  },
                  {
                    key: 'type',
                    header: 'Type',
                    render: (item) =>
                      TransactionTypeBadge(item.map((t) => t.type)),
                  },
                  {
                    key: 'amount',
                    header: 'Amount',
                    render: (item) =>
                      html.span(
                        attr.class(
                          item.map((t) =>
                            t.amount > 0
                              ? 'text-sm font-medium text-green-600'
                              : 'text-sm font-medium text-red-600'
                          )
                        ),
                        item.map((t) =>
                          formatCredits(t.amount, { includeSign: true })
                        )
                      ),
                  },
                ],
                data: transactions,
                renderMobileCard: (txn) => TransactionCard(txn),
                emptyMessage: 'No transactions yet.',
              })
            )
          )
      )
    ),
  })
}

// ============================================================================
// Utility Components
// ============================================================================

function TransactionTypeBadge(type: Signal<'purchase' | 'usage' | 'refund'>) {
  const variantMap: Record<string, BadgeVariant> = {
    purchase: 'success',
    usage: 'neutral',
    refund: 'info',
  }

  return StatusBadge({
    label: type.map(capitalize),
    variant: type.map((t) => variantMap[t] ?? 'neutral'),
  })
}

function TransactionCard(txn: Signal<Transaction>) {
  return html.div(
    attr.class('bg-white border border-gray-200 rounded-lg p-4'),
    html.div(
      attr.class('flex items-start justify-between'),
      html.div(
        html.h3(
          attr.class('font-medium text-gray-900'),
          txn.map((t) => t.description)
        ),
        html.p(
          attr.class('text-sm text-gray-500 mt-1'),
          txn.map((t) => new Date(t.timestamp).toLocaleString())
        )
      ),
      html.div(
        attr.class('text-right'),
        html.div(
          attr.class('mb-2'),
          TransactionTypeBadge(txn.map((t) => t.type))
        ),
        html.p(
          attr.class(
            txn.map((t) =>
              t.amount > 0
                ? 'text-sm font-medium text-green-600'
                : 'text-sm font-medium text-red-600'
            )
          ),
          txn.map((t) => formatCredits(t.amount, { includeSign: true }))
        )
      )
    )
  )
}
